import { Injectable, OnDestroy } from '@angular/core';
import { combineLatest, Observable, Subject, of } from 'rxjs';
import { filter, takeUntil, map } from 'rxjs/operators';
import { TranslateService } from '@shared/translate/translate.service';
import { LiteratureDataService } from '@literature/services/literature-data.service';

import { IUserProfile, UserRole } from '@services/profile.interface';
import { LiteratureDocument, LiteratureDocumentListingData } from '@types';
import { SH } from '@utils/app.constants';
export interface DocumentList {
  documents: DocumentEntity[];
  documents2: DocumentEntity[];
  scrollId: string;
  viewAllTitle: string;
}

export interface DocumentEntity {
  title: string;
  docType: string;
  href: string;
  type: string;
  protected: boolean;
  publicationDate: string;
}

export interface DocumentsConfig extends Array<string> {}

const MAX_ELEMENTS = 6;
@Injectable({
  providedIn: 'root',
})
export class HeaderDocumentsService implements OnDestroy {
  public documentList: DocumentList;

  private documentListingDataInput$: Subject<LiteratureDocumentListingData[]>;
  // TODO remove it - it is only for local development when documents are not available
  public documentListingDataInput2$: Subject<{}[]>;
  private documentConfig$: Subject<DocumentsConfig>;
  public documentListingData$: Observable<DocumentEntity[]>;
  public showLogIn = true;
  private unsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private translateService: TranslateService,
    private literatureDataService: LiteratureDataService
  ) {
    // initializes documents list
    this.documentListingDataInput$ = new Subject<
      LiteratureDocumentListingData[]
    >();

    // TODO remove it - it is only for local development when documents are not available
    this.documentListingDataInput2$ = new Subject<
      LiteratureDocumentListingData[]
    >();

    // initializes documents config
    this.documentConfig$ = new Subject<DocumentsConfig>();

    // reads data about logged user
    this.literatureDataService
      .getProfileData$()
      ?.pipe(
        takeUntil(this.unsubscribe$),
        filter((profile) => !!profile)
      )
      .subscribe((data: IUserProfile) => {
        this.showLogIn =
          !data?.isLoggedIn ||
          UserRole[data?.profileInfo?.role] === SH.toLocaleLowerCase();
      });

    // reads documents from literature service
    this.documentListingData$ = combineLatest([
      this.documentConfig$,
      this.documentListingDataInput$,
    ]).pipe(
      takeUntil(this.unsubscribe$),
      map((result) => {
        return this.getDocuments(result[0], result[1]);
      })
    );
  }

  /**
   * returns documents - filtered and sorted
   * @param documents array of documents
   * @param segmentId current segment
   */
  public getDocuments(
    config: DocumentsConfig,
    documents: LiteratureDocumentListingData[]
  ): DocumentEntity[] {
    // TODO - optimize translations
    // forach document type in config translate item.

    return documents
      .filter(({ literatureData }) => {
        return (
          config.includes(literatureData.dctermsType) &&
          this.isDocumentDownloadValid(literatureData)
        );
      })
      .map(({ literatureData }) => {
        return this.mapDocumentObject(literatureData);
      })
      .sort(this.documentsSort(config))
      .filter((v, i, a) => a.findIndex((t) => t.docType === v.docType) === i) // filters only one document of specific type
      .slice(0, MAX_ELEMENTS); // takes max 4 elements
  }

  /**
   *  maps lierature object to fund header object
   * @param literatureData source data
   */
  mapDocumentObject(literatureData: LiteratureDocument): DocumentEntity {
    return {
      title: `${this.translateService.instant(
        'literature.' + literatureData.dctermsType
      )}`,
      docType: literatureData.dctermsType,
      publicationDate: literatureData.publicationDate,
      href: this.getDownloadLink(literatureData),
      type: literatureData.fileExtension,
      protected: this.isLoginRequired(literatureData?.loginRequired),
    };
  }

  private isLoginRequired(litLoginRequired: boolean): boolean {
    return litLoginRequired && this.showLogIn;
  }
  /**
   * returns empty string if SIGNIN is required
   * @param litDocument literature document object
   */
  getDownloadLink(litDocument: LiteratureDocument) {
    return this.isLoginRequired(litDocument?.loginRequired)
      ? ''
      : litDocument.pdfDownloadLink;
  }

  /**
   * sorts by config order and then by publicationDate desc
   * @param literature header config
   */
  private documentsSort = (config) => (a, b) => {
    const a1 = config.indexOf(a.docType);
    const a2 = config.indexOf(b.docType);
    const b1 = a.publicationDate;
    const b2 = b.publicationDate;

    return a1 - a2 || b2.localeCompare(b1);
  };

  /**
   * verifies if link is valid - this method may be extended in the future
   */
  private isDocumentDownloadValid(litDocument: LiteratureDocument) {
    return (
      litDocument?.pdfDownloadLink?.length > 1 &&
      litDocument?.docId &&
      litDocument.downloadUrl
    );
  }
  /**
   *  updates documents with data read from literature service
   */
  public updateDocuments(listingData: LiteratureDocumentListingData[]) {
    this.documentListingDataInput$.next(listingData);
  }

  /**
   * updates config with data read from literature service
   */
  public updateConfig(config: DocumentsConfig) {
    this.documentConfig$.next(config);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
